import { IGenericModelFields } from "./IGenericFields";

export enum UserExperienceState {
    /**
     * The experience has started (happening right now)
     */
    STARTED = "started",
    /**
     * The experience has completed (finished successfully)
     */
    COMPLETED = "completed",
    /**
     * The experience has been interrupted by another user experience
     */
    INTERRUPTED = "interrupted",
    /**
     * The experience has been abandoned (e.g. the user closed the browser or clicked back)
     */
    ABANDONED = "abandoned",
    /**
     * The experience has timed out, marked only automatically after a certain duration (set by MaxUserExperienceDurationsMinutes)
     */
    TIMED_OUT = "timedOut",
    /**
     * The experience has failed (e.g. the user encountered an error)
     */
    FAILED = "failed"
}

export enum UserExperienceType {
    CREATE_PRESENTATION = "createPresentation",
    ADD_SLIDES_TO_PRESENTATION = "addSlideToPresentation",
    EXPORT_PRESENTATION = "exportPresentation",
    DRAG_SLIDE_ELEMENT = "dragSlideElement",
    DRAG_SLIDE_ELEMENT_TEXT_BLOCK = "dragSlideElementTextBlock",
    DRAG_PRESENTATIONS_TO_FOLDER = "dragPresentationsToFolder",
    CHECKOUT = "checkout",
    INVITE_USERS_TO_WORKSPACE = "inviteUsersToWorkspace",
    ADD_ASSET_TO_SLIDE_ELEMENT = "addAssetToSlideElement",
    TRANSLATE = "translate",
    GENERATE_TEXT = "generateText"
}

export const MaxUserExperienceDurationsMinutes: Record<UserExperienceType, number> = {
    [UserExperienceType.CREATE_PRESENTATION]: 20,
    [UserExperienceType.EXPORT_PRESENTATION]: 30,
    [UserExperienceType.ADD_SLIDES_TO_PRESENTATION]: 10,
    [UserExperienceType.DRAG_SLIDE_ELEMENT]: 2,
    [UserExperienceType.DRAG_SLIDE_ELEMENT_TEXT_BLOCK]: 2,
    [UserExperienceType.DRAG_PRESENTATIONS_TO_FOLDER]: 2,
    [UserExperienceType.CHECKOUT]: 20,
    [UserExperienceType.INVITE_USERS_TO_WORKSPACE]: 20,
    [UserExperienceType.ADD_ASSET_TO_SLIDE_ELEMENT]: 10,
    [UserExperienceType.TRANSLATE]: 30,
    [UserExperienceType.GENERATE_TEXT]: 5
};

export const UserExperienceSubtype = {
    [UserExperienceType.CREATE_PRESENTATION]: {
        CREATE_BLANK: "createBlank",
        GENERATE_WITH_AI: "generateWithAi",
        COPY_TEAM_TEMPLATE: "copyTeamTemplate",
        COPY_STARTER_TEMPLATE: "copyStarterTemplate",
        IMPORT_FROM_PPT: "importFromPpt"
    },
    [UserExperienceType.ADD_SLIDES_TO_PRESENTATION]: {
        ADD_FROM_TEMPLATE: "addFromTemplate",
        ADD_SHARED_SLIDES: "addSharedSlides",
        COPY_TEAM_TEMPLATES: "copyTeamTemplates",
        ADD_SHARED_SLIDES_AND_COPY_TEAM_TEMPLATES: "addSharedSlidesAndCopyTeamTemplates",
        COPY_GALLERY_SLIDES: "copyGallerySlides",
        COPY_EXISTING_SLIDES: "copyExistingSlides",
        GENERATE_WITH_AI: "generateWithAi",
        IMPORT_FROM_PPT: "importFromPpt"
    },
    [UserExperienceType.EXPORT_PRESENTATION]: {
        EXPORT_TO_PDF: "exportToPdf",
        PRINT_TO_PDF: "printToPdf",
        EXPORT_TO_PPTX: "exportToPptx",
        EXPORT_TO_PPTX_EDITABLE: "exportToPptxEditable",
        EXPORT_OFFLINE: "exportOffline",
        EXPORT_TO_GOOGLE_SLIDES: "exportToGoogleSlides",
        EXPORT_COPY_LINK: "exportCopyLink",
    },
    [UserExperienceType.DRAG_SLIDE_ELEMENT]: {
        DRAG_RESIZE: "dragResize",
        DRAG_POSITION: "dragPosition"
    },
    [UserExperienceType.ADD_ASSET_TO_SLIDE_ELEMENT]: {
        ADD_RECENTLY_USED_MEDIA: "addRecentlyUsedMedia",
        ADD_ICON: "addIcon",
        ADD_LOGO: "addLogo",
        ADD_AI_SEARCH: "addAiSearch",
        ADD_STOCK_PHOTO: "addStockPhoto",
        ADD_STOCK_VIDEO: "addStockVideo",
        ADD_WEB_IMAGE: "addWebImage",
        SEARCH_LOGOS: "searchLogos",
        ADD_FROM_PRESENTATION: "addFromPresentation",
        ADD_TEAM_ASSET: "addTeamAsset",
        IMPORT_MEDIA: "importMedia"
    },
    [UserExperienceType.TRANSLATE]: {
        TRANSLATE_TEXT: "translateText",
        TRANSLATE_SLIDE: "translateSlide",
        TRANSLATE_PRESENTATION: "translatePresentation"
    }
};

interface IMetadata {
    sessionId: string;
    browserId: string;
    releaseVersion: string;
    buildVersion: string;
    clientName: string;
    uid?: string;
    workspaceId?: string;
    isMobile?: boolean;
    error?: {
        name?: string;
        message?: string;
        stack?: string;
        [key: string]: any;
    }
    [key: string]: any;
}

export interface IUserExperience extends IGenericModelFields {
    type: UserExperienceType;
    state: UserExperienceState;
    stateModifiedAt: number;
    stateHistory: {
        state: UserExperienceState;
        timestamp: number;
    }[];
    metadata: IMetadata;
}
